'use client';

import { useEffect, useMemo, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';

type TimeLeft = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

export type CountdownTimerProps = {
  countdown: Date | string | null | undefined;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  className?: string;
};

export default function CountdownTimer({
  countdown,
  title,
  subtitle,
  className = '',
}: CountdownTimerProps) {
  const { t } = useTranslation('common');
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null);

  const target = useMemo(() => {
    if (!countdown) return null;
    if (countdown instanceof Date) return countdown.getTime();
    const s = String(countdown);
    const parts = s.split('/');
    if (parts.length === 3) {
      const [dd, mm, yyyy] = parts.map(Number);
      return new Date(yyyy, (mm ?? 1) - 1, dd ?? 1).getTime();
    }
    const n = new Date(s).getTime();
    return Number.isFinite(n) ? n : null;
  }, [countdown]);

  useEffect(() => {
    if (target === null) return;

    const tick = () => {
      const now = Date.now();
      const diff = Math.max(0, target - now);

      const days = Math.floor(diff / 86_400_000);
      const hours = Math.floor((diff % 86_400_000) / 3_600_000);
      const minutes = Math.floor((diff % 3_600_000) / 60_000);
      const seconds = Math.floor((diff % 60_000) / 1_000);

      if (diff <= 0) {
        setTimeLeft(null);
        return;
      }
      setTimeLeft({ days, hours, minutes, seconds });
    };

    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [target]);

  if (!countdown || !timeLeft) return null;
  const z2 = (n: number) => String(n).padStart(2, '0');

  return (
    <div className={className}>
      {title && <div className="text-[24px] font-bold">{title}</div>}

      <div className="my-2 flex justify-center gap-8">
        <div className="flex flex-col">
          <p className="text-[28px]">{z2(timeLeft.days)}</p>
          <p className="text-[10px] font-bold uppercase">
            {t('popupDates.days')}
          </p>
        </div>
        <div className="flex flex-col">
          <p className="text-[28px]">{z2(timeLeft.hours)}</p>
          <p className="text-[10px] font-bold uppercase">
            {t('popupDates.hours')}
          </p>
        </div>
        <div className="flex flex-col">
          <p className="text-[28px]">{z2(timeLeft.minutes)}</p>
          <p className="text-[10px] font-bold uppercase">
            {t('popupDates.minutes')}
          </p>
        </div>
        <div className="flex flex-col">
          <p className="text-[28px]">{z2(timeLeft.seconds)}</p>
          <p className="text-[10px] font-bold uppercase">
            {t('popupDates.secs')}
          </p>
        </div>
      </div>

      {subtitle && <div className="opacity-70">{subtitle}</div>}
    </div>
  );
}
